import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Spinner } from '@/components/ui/spinner'
import { normalizeFacts } from '../utils/factsUtils'
import type { FactsPayload } from '../types'
import { FactCard, FactsLegend } from './FactCard'
interface CallLogsSheetFactsProps {
  facts: FactsPayload | null
  isLoading: boolean
  error: string | null
  onRetry: () => void
}

/** Stopgap Facts panel for the Call Logs sheet. */
export function CallLogsSheetFacts({
  facts,
  isLoading,
  error,
  onRetry,
}: CallLogsSheetFactsProps) {
  if (error) {
    return (
      <div className="flex items-center justify-between py-2">
        <span className="text-muted-foreground text-sm">
          Failed to load facts
        </span>
        <Button
          size="sm"
          variant="outline"
          onClick={onRetry}
          disabled={isLoading}>
          {isLoading ? 'Retrying…' : 'Retry'}
        </Button>
      </div>
    )
  }

  if (isLoading && facts == null) {
    return (
      <div className="flex justify-center py-4">
        <Spinner />
      </div>
    )
  }

  const items = normalizeFacts(facts)

  if (items.length === 0) {
    // Explicit empty state so the section doesn't flicker on settled-empty calls.
    return (
      <div className="bg-neutral-subtle-bg rounded-md px-3 py-6 text-center text-sm text-muted-foreground">
        No extracted facts.
      </div>
    )
  }

  return <FactsList items={items} />
}

function FactsList({ items }: { items: ReturnType<typeof normalizeFacts> }) {
  const [filter, setFilter] = useState('')
  const visible = filter
    ? items.filter((f) => f.name.toLowerCase().includes(filter.toLowerCase()))
    : items

  return (
    <>
      <Input
        className="mb-2"
        placeholder="Filter facts..."
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
      />
      {items.length > 0 && <FactsLegend />}
      {visible.length === 0 && filter !== '' ? (
        <p className="text-muted-foreground py-2 text-sm">
          No facts match &ldquo;{filter}&rdquo;.
        </p>
      ) : (
        <div className="flex flex-col gap-2">
          {visible.map((fact) => (
            <FactCard
              key={`${fact.name}-${fact.evidence_span[0]}-${fact.evidence_span[1]}`}
              item={fact}
            />
          ))}
        </div>
      )}
    </>
  )
}
